/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : DynamicFormTemplate.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import React, { useEffect } from "react";
import PropTypes from "prop-types";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import customConsole from "../../config/customConsole";
import { EDIT_TYPES } from "../../config/appConstants";
import CustomFormTextFieldV2 from "../customComponentsV2/common/textField/CustomFormTextFieldV2";
import { fontSizeDetails } from "../../config/appTheme";

const getFieldColor = (editType) => {
  if (editType === EDIT_TYPES?.DDH) return "blue";
  if (editType === EDIT_TYPES?.SNOW) return "grey";
  return "black";
};

const DynamicFormTemplate = ({
  showOnlyTemplate = false,
  showFieldsUpdate = false,
  handleEditUpdateField = () => { },
  handleEditField,
  handleDeleteField,
  headerOptionsList,
  signatureOptionsList,
  footerOptionsList,
  selectedHeaders,
  selectedSignatures,
  selectedFooters,
  dynamicFormData,
  setDynamicFormData,
}) => {
  useEffect(() => {
    customConsole({ dynamicFormData });
  }, [dynamicFormData]);

  const updateHeaderTitle = (header, position, value) => {
    return header?.position === position ? { ...header, title: value } : header;
  };
  const headerTitleFunction = (headers, item, value) => {
    const updatedHeaders = headers.map((header) =>
      updateHeaderTitle(header, item?.position, value),
    );

    const isExists = headers.some(
      (header) => header?.position === item?.position,
    );

    const newHeader = isExists
      ? []
      : [
        {
          image: "",
          position: item?.position,
          title: value,
          sub_title: "",
        },
      ];

    return { updatedHeaders, newHeader };
  };

  const updateHeaderSubtitle = (prev, value, item) => {
    const headers = prev?.headers || [];

    const exists = headers.some(
      (header) => header?.position === item?.position,
    );

    const updatedHeaders = headers.map((header) => {
      if (header?.position === item?.position) {
        return { ...header, sub_title: value };
      }
      return header;
    });

    if (!exists) {
      updatedHeaders.push({
        image: "",
        position: item?.position,
        title: "",
        sub_title: value,
      });
    }

    return {
      ...prev,
      headers: updatedHeaders,
    };
  };

  const updateFields = (prev, value, field) => {
    const updatedFields = (prev?.fields || []).map((field1) => {
      if (field1?.sequence === field?.sequence) {
        return { ...field1, value };
      }
      return field1;
    });

    return {
      ...prev,
      fields: updatedFields,
    };
  };

  const updateSignatureTitle = (prev, value, item) => {
    const signatures = prev?.signatures || [];

    const exists = signatures.some(
      (signature) => signature?.position === item?.position,
    );

    const updatedSignatures = signatures.map((signature) => {
      if (signature?.position === item?.position) {
        return { ...signature, title: value };
      }
      return signature;
    });

    if (!exists) {
      updatedSignatures.push({
        image: "",
        position: item?.position,
        title: value,
        sub_title: "",
      });
    }

    return {
      ...prev,
      signatures: updatedSignatures,
    };
  };

  const updateSignatureSubTitle = (prev, value, item) => {
    const signatures = prev?.signatures || [];

    const exists = signatures.some(
      (signature) => signature?.position === item?.position,
    );

    const updatedSignatures = signatures.map((signature) => {
      if (signature?.position === item?.position) {
        return { ...signature, sub_title: value };
      }
      return signature;
    });

    if (!exists) {
      updatedSignatures.push({
        image: "",
        position: item?.position,
        title: "",
        sub_title: value,
      });
    }

    return {
      ...prev,
      signatures: updatedSignatures,
    };
  };

  const updateFooterTitle = (prev, value, item) => {
    const footers = prev?.footers || [];

    const exists = footers.some(
      (footer) => footer?.position === item?.position,
    );

    const updatedFooters = footers.map((footer) => {
      if (footer?.position === item?.position) {
        return { ...footer, title: value };
      }
      return footer;
    });

    if (!exists) {
      updatedFooters.push({
        image: "",
        position: item?.position,
        title: value,
        sub_title: "",
      });
    }

    return {
      ...prev,
      footers: updatedFooters,
    };
  };

  const updateFooterSubTitle = (prev, value, item) => {
    const footers = prev?.footers || [];

    const exists = footers.some(
      (footer) => footer?.position === item?.position,
    );

    const updatedFooters = footers.map((footer) => {
      if (footer?.position === item?.position) {
        return { ...footer, sub_title: value };
      }
      return footer;
    });

    if (!exists) {
      updatedFooters.push({
        image: "",
        position: item?.position,
        title: "",
        sub_title: value,
      });
    }

    return {
      ...prev,
      footers: updatedFooters,
    };
  };

  return (
    <Paper
      elevation={2}
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        border: "1px solid grey",
        borderRadius: 1,
      }}
    >
      <Box sx={{ p: 1.4, display: "flex", gap: 2 }}>
        {headerOptionsList?.map((item) => (
          <Box
            key={item?.position}
            sx={{
              visibility: selectedHeaders?.includes(item?.position)
                ? "visible"
                : "hidden",
            }}
          >
            <CustomFormTextFieldV2
              label="Header Title"
              required
              fldType="text"
              fullWidth
              id="header-title-input"
              type="text"
              size="small"
              value={
                dynamicFormData?.headers?.find(
                  (el) => el?.position === item?.position,
                )?.title || ""
              }
              placeholder="Header Title*"
              setValue={(value) => {
                setDynamicFormData((prev) => {
                  const headers = prev?.headers || [];
                  const { updatedHeaders, newHeader } = headerTitleFunction(
                    headers,
                    item,
                    value,
                  );
                  return {
                    ...prev,
                    headers: [...updatedHeaders, ...newHeader],
                  };
                });
              }}
            />

            <CustomFormTextFieldV2
              fullWidth
              label="Header Subtitle"
              type="text"
              size="small"
              value={
                dynamicFormData?.headers?.find(
                  (el) => el?.position === item?.position,
                )?.sub_title || ""
              }
              placeholder="Header Subtitle*"
              setValue={(value) =>
                setDynamicFormData((prev) =>
                  updateHeaderSubtitle(prev, value, item),
                )
              }
            />
          </Box>
        ))}
      </Box>
      <Divider sx={{ border: "1px solid lightGrey" }} />
      <Box sx={{ flexGrow: 1, minHeight: "150px", m: 2.5, mb: 3.4 }}>
        {dynamicFormData?.fields
          ?.slice()
          ?.sort((a, b) => a.sequence - b.sequence)
          ?.map((field) => (
            <Box
              key={field?.sequence}
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                mb: 1.4,
              }}
            >
              <Typography
                variant="subtitle1"
                color={getFieldColor(field?.edit_type)}
                sx={{ fontSize: fontSizeDetails.body1.fontSize }}
              >
                {`${field?.sequence}. ${field?.field_name}: `}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: -1,
                }}
              >
                <TextField
                  InputProps={{
                    readOnly: showOnlyTemplate,
                    disableUnderline: showOnlyTemplate,
                  }}
                  disabled={field?.edit_type === EDIT_TYPES?.SNOW}
                  fullWidth
                  size="large"
                  variant="standard"
                  type={field?.field_type || "string"}
                  value={field?.value}
                  onChange={(e) =>
                    setDynamicFormData((prev) =>
                      updateFields(prev, e?.target?.value, field),
                    )
                  }
                  sx={{ ml: 1 }}
                />
                {showFieldsUpdate &&
                  field?.value?.length < 1 &&
                  field?.edit_type !== EDIT_TYPES?.SNOW && (
                    <IconButton
                      size="small"
                      onClick={() => handleEditUpdateField(field?.field_name)}
                    >
                      <EditIcon sx={{ color: getFieldColor(field?.edit_type) }} />
                    </IconButton>
                  )}
                {showOnlyTemplate ? null : (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditField(field?.sequence)}
                    >
                      <EditIcon
                        sx={{
                          fontSize: 16,
                          color: getFieldColor(field?.edit_type),
                        }}
                      />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteField(field?.sequence)}
                    >
                      <DeleteIcon sx={{ color: "black", fontSize: 16 }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
      </Box>
      <Box
        sx={{
          p: 1.4,
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          gap: 4,
        }}
      >
        {signatureOptionsList?.map((item) => (
          <Box
            key={item?.position}
            sx={{
              visibility: selectedSignatures?.includes(item?.position)
                ? "visible"
                : "hidden",
            }}
          >
            <CustomFormTextFieldV2
              label="Signature Title"
              fullWidth
              fldType="text"
              size="small"
              value={
                dynamicFormData?.signatures?.find(
                  (el) => el?.position === item?.position,
                )?.title || ""
              }
              placeholder="Signature Title*"
              disabled={showOnlyTemplate}
              setValue={(value) =>
                setDynamicFormData((prev) =>
                  updateSignatureTitle(prev, value, item),
                )
              }
            />

            <CustomFormTextFieldV2
              label="Signature Subtitle"
              fullWidth
              fldType="text"
              size="small"
              value={
                dynamicFormData?.signatures?.find(
                  (el) => el?.position === item?.position,
                )?.sub_title || ""
              }
              placeholder="Signature Subtitle*"
              disabled={showOnlyTemplate}
              sx={{ mt: 2, mb: 0.4 }}
              setValue={(value) =>
                setDynamicFormData((prev) =>
                  updateSignatureSubTitle(prev, value, item),
                )
              }
            />
          </Box>
        ))}
      </Box>
      <Divider sx={{ border: "1px solid lightGrey" }} />
      <Box
        sx={{
          p: 1.4,
          display: "flex",
          gap: 2,
          justifyContent: "center",
          mt: "auto",
        }}
      >
        {footerOptionsList?.map((item) => (
          <Box
            key={item?.position}
            sx={{
              visibility: selectedFooters?.includes(item?.position)
                ? "visible"
                : "hidden",
            }}
          >
            <CustomFormTextFieldV2
              label="Footer Title"
              fullWidth
              fldType="text"
              size="small"
              value={
                dynamicFormData?.footers?.find(
                  (el) => el?.position === item?.position,
                )?.title || ""
              }
              placeholder="Footer Title*"
              disabled={showOnlyTemplate}
              setValue={(value) =>
                setDynamicFormData((prev) =>
                  updateFooterTitle(prev, value, item),
                )
              }
            />

            <CustomFormTextFieldV2
              label="Footer Subtitle"
              fullWidth
              fldType="text"
              size="small"
              value={
                dynamicFormData?.footers?.find(
                  (el) => el?.position === item?.position,
                )?.sub_title || ""
              }
              placeholder="Footer Subtitle*"
              disabled={showOnlyTemplate}
              sx={{ mt: 2, mb: 0.4 }}
              setValue={(value) =>
                setDynamicFormData((prev) =>
                  updateFooterSubTitle(prev, value, item),
                )
              }
            />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

DynamicFormTemplate.propTypes = {
  showOnlyTemplate: PropTypes.bool,
  showFieldsUpdate: PropTypes.bool,
  handleEditUpdateField: PropTypes.func,
  handleEditField: PropTypes.func,
  handleDeleteField: PropTypes.func,
  headerOptionsList: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({ position: PropTypes.string })),
    PropTypes.shape({ map: PropTypes.func }),
  ]),
  signatureOptionsList: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({ position: PropTypes.string })),
    PropTypes.shape({ map: PropTypes.func }),
  ]),
  footerOptionsList: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({ position: PropTypes.string })),
    PropTypes.shape({ map: PropTypes.func }),
  ]),
  selectedHeaders: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.shape({ includes: PropTypes.func }),
  ]),
  selectedSignatures: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.shape({ includes: PropTypes.func }),
  ]),
  selectedFooters: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.shape({ includes: PropTypes.func }),
  ]),
  dynamicFormData: PropTypes.shape({
    headers: PropTypes.oneOfType([
      PropTypes.arrayOf(
        PropTypes.shape({
          position: PropTypes.string,
          title: PropTypes.string,
          sub_title: PropTypes.string,
        }),
      ),
      PropTypes.shape({ find: PropTypes.func }),
    ]),
    signatures: PropTypes.oneOfType([
      PropTypes.arrayOf(
        PropTypes.shape({
          position: PropTypes.string,
          title: PropTypes.string,
          sub_title: PropTypes.string,
        }),
      ),
      PropTypes.shape({ find: PropTypes.func }),
    ]),
    footers: PropTypes.oneOfType([
      PropTypes.arrayOf(
        PropTypes.shape({
          position: PropTypes.string,
          title: PropTypes.string,
          sub_title: PropTypes.string,
        }),
      ),
      PropTypes.shape({ find: PropTypes.func }),
    ]),
    fields: PropTypes.oneOfType([
      PropTypes.arrayOf(
        PropTypes.shape({
          sequence: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          field_name: PropTypes.string,
          value: PropTypes.any,
          edit_type: PropTypes.any,
          field_type: PropTypes.string,
        }),
      ),
      PropTypes.shape({ slice: PropTypes.func }),
    ]),
  }),
  setDynamicFormData: PropTypes.func,
};

export default DynamicFormTemplate;
